import {
  datevSupplierExport,
  exportSuppliersToDATEV,
} from "@/apis/custom/customRoutes";
import { useTable } from "@/components/shared/data-table/useTable";
import { DatevExportType } from "@/features/settings/export-history/cells/exportType";
import type { TranslationKey } from "@/localisation/types";
import { useGetAllExports } from "@/queries/exports/get-all.legacy";
import { useDeleteSupplier } from "@/queries/suppliers/delete";
import { useGetAllSuppliers } from "@/queries/suppliers/get-all";
import { useUpdateSupplier } from "@/queries/suppliers/update";
import * as Sentry from "@sentry/react";
import type { ColumnFilter } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { buildColumns } from "./columns";

export type CounterpartiesTableInstance = ReturnType<
  typeof useCounterpartiesTable
>;

export function useCounterpartiesTable({
  tableName,
  type,
  showImportButton = true,
}: {
  tableName: string;
  type: "creditor" | "debtor";
  showImportButton: boolean;
}) {
  const { t } = useTranslation();

  const { data, loading, error, refetch } = useGetAllSuppliers({ type });
  const deleteSuppliers = useDeleteSupplier();
  const [updateSupplier] = useUpdateSupplier({
    config: { toasts: { loading: true } },
  });

  const { exports } = useGetAllExports();

  // FIXME: this is not the best way to get the exports results errors. we should fix it.
  const suppliersExportErrors = useMemo(() => {
    if (!exports) return [];

    const [latestExport] = [...exports]
      .filter(
        (exp) =>
          [DatevExportType.BUCHUNGSDATENSERVICE_SUPPLIERS].includes(
            exp.exportType,
          ) && exp.state === "failed",
      )
      .sort(
        (a, b) =>
          new Date(b.exportedAt).getTime() - new Date(a.exportedAt).getTime(),
      );

    return latestExport;
  }, [exports]);

  const dataWithExportErrors = useMemo(() => {
    return data.map((supplier) => ({
      ...supplier,
      exportErrors: suppliersExportErrors,
    }));
  }, [data, suppliersExportErrors]);

  const [globalFilter, setGlobalFilter] = useState("");
  const [statusFilterValues, setStatusFilterValues] = useState<string[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);

  const columns = useMemo(() => buildColumns(type), [type]);

  const table = useTable({
    data: dataWithExportErrors,
    columns,
    tableName,
    getRowId: (row) => row.supplierId ?? "",
    globalFilter,
    onGlobalFilterChange: setGlobalFilter,
    columnFilters,
    onColumnFilterChange: setColumnFilters,
    initialSorting: [{ id: "supplierName", desc: false }],
  });

  const handleStatusFilterChange = (filterValues: string[]) => {
    setStatusFilterValues(filterValues);
    table.setColumnFilters([{ id: "status", value: filterValues }]);
  };

  const handleDelete = async (supplierIds: string[]) => {
    try {
      await deleteSuppliers(supplierIds);
      table.setRowSelection({});
      await refetch();
    } catch (error) {
      Sentry.captureException(error);
      toast.error(t("generic_error"), { richColors: true });
    }
  };

  const handleExportCsv = async (supplierIds: string[]) => {
    const toastId = toast.loading(t("generic_loading"));
    try {
      await datevSupplierExport(supplierIds);
      toast.success(t("generic_success"), {
        id: toastId,
        richColors: true,
      });
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
    }
  };

  const handleExportDatev = async (supplierIds: string[]) => {
    const toastId = toast.loading(t("generic_loading"));
    try {
      await exportSuppliersToDATEV(supplierIds);
      toast.success(t("generic_success"), {
        id: toastId,
        richColors: true,
      });
    } catch (error: any) {
      const errorMessage = error?.response?.data?.data?.code
        ? t(`server_errors.${error.response.data.data.code}` as TranslationKey)
        : t("generic_error");
      toast.error(errorMessage as string, { id: toastId, richColors: true });
    }
  };

  const handleUpdate = async (supplierId: string, updatedFields: any) => {
    await updateSupplier({
      supplierId,
      updatedFields,
    });
  };

  const selectedSupplier = useMemo(() => {
    if (table.focusedRowId !== undefined) {
      return (
        data.find((supplier) => supplier.supplierId === table.focusedRowId) ??
        null
      );
    }
    return null;
  }, [data, table.focusedRowId]);

  const handleSelectSupplier = (supplierId: string | null) => {
    table.table.setFocusedRow(supplierId ?? undefined);
  };

  const handleCreated = async () => {
    await refetch();
  };

  const handleImported = async () => {
    await refetch();
  };

  return {
    ...table,
    type,
    loading,
    showImportButton,
    globalFilter,
    setGlobalFilter,
    statusFilterValues,
    onStatusFilterChange: handleStatusFilterChange,
    onDelete: handleDelete,
    onExportCsv: handleExportCsv,
    onExportDatev: handleExportDatev,
    onUpdate: handleUpdate,
    selectedSupplier,
    onSelectSupplier: handleSelectSupplier,
    onCreated: handleCreated,
    onImported: handleImported,
  };
}
